// Date formatting utilities
export const formatDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Relative time for notifications
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Status colors
export const getStatusColor = (status) => ({
  'Open': '#2563eb',
  'In Progress': '#7c3aed',
  'Resolved': '#16a34a',
  'Closed': '#6b7280',
  'Reopen': '#dc2626',
  'Cancelled': '#374151',
  'Recommend Cancel': '#f59e0b',
  'Submitted': '#3b82f6',
  'Under Review': '#8b5cf6',
  'Planned': '#06b6d4',
  'Testing': '#10b981',
  'Completed': '#22c55e',
  'Rejected': '#ef4444'
}[status] || '#6b7280');

// Priority colors
export const getPriorityColor = (priority) => ({
  'Critical': '#dc2626',
  'High': '#ea580c',
  'Medium': '#ca8a04',
  'Low': '#16a34a'
}[priority] || '#6b7280');

// User name extraction
export const getUserNameByEmail = (email) => {
  if (!email) return null;
  return email.split('@')[0];
};

// Find user by email in users array
export const findUserByEmail = (users, email) => {
  if (!email || !users) return null;
  return users.find(u => u.email === email);
};

// Priority order for sorting
export const PRIORITY_ORDER = {
  'Critical': 4,
  'High': 3,
  'Medium': 2,
  'Low': 1
};

// Status order for board display
export const STATUS_ORDER = [
  'Open',
  'In Progress',
  'Resolved',
  'Closed',
  'Reopen',
  'Recommend Cancel',
  'Cancelled'
];

// Role badge class helper
export const getRoleBadgeClass = (role) => {
  const baseClass = 'role-badge';
  switch (role) {
    case 'Admin': return `${baseClass} role-badge--admin`;
    case 'Tester/QA': return `${baseClass} role-badge--tester`;
    case 'Customer': return `${baseClass} role-badge--customer`;
    default: return `${baseClass} role-badge--unassigned`;
  }
};

// Initials of an application name. "Web Portal" → "WP", "Mobile" → "MO".
// For multi-word names: first letter of each word (max 4 chars to keep badges short).
// For single-word names: first 2 letters, uppercased.
// Returns null if no usable name is provided.
export const getApplicationInitials = (applicationName) => {
  if (!applicationName || typeof applicationName !== 'string') return null;
  const trimmed = applicationName.trim();
  if (!trimmed) return null;

  const words = trimmed.split(/\s+/).filter(w => /[A-Za-z]/.test(w));
  if (words.length === 0) return null;

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return words.map(w => w[0]).join('').slice(0, 4).toUpperCase();
};

// Format a sequential displayNumber as "{INITIALS}-{D|F}.{n}", e.g. "WP-D.1".
// `kind` is 'D' for defects, 'F' for feature requests.
// Falls back to bare "{kind}.{n}" if app initials can't be derived,
// and to null if no displayNumber is available at all.
export const formatDisplayNumber = (item, kind) => {
  if (!item || typeof item.displayNumber !== 'number') return null;
  const initials = getApplicationInitials(item.applicationName);
  return initials
    ? `${initials}-${kind}.${item.displayNumber}`
    : `${kind}.${item.displayNumber}`;
};
